import { drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from '../engine/games/luck';
import type { CasinoGame, GameId } from '../engine/games/types';
import type { Rng } from '../engine/rng';

/**
 * Pachinko (web only, user 2026-09-27: the glasses keep Plinko on the
 * Penthouse). Written against the engine's CasinoGame interface but kept out
 * of src/engine, which stays byte-identical with the glasses game.
 *
 * One play buys a stash of BALLS balls (user, 2026-09-27: "like actual
 * pachinko", a steady stream, not a quick handful), each worth 1/BALLS of
 * the bet, fired one after another. A ball falls out, drops into a side
 * tulip (TULIP_PAYS balls back), or hits the START pocket (1 ball back and a
 * spin of the three-digit screen). Three of a kind is FEVER: the big gate
 * opens and FEVER_PAYS balls rain into the tray, worked out so the machine
 * returns exactly TARGET_RTP.
 */
export const PACHINKO = 'pachinko' as GameId;
export const BALLS = 50;
export const TARGET_RTP = 0.96;
export const P_TULIP = 0.12;
export const TULIP_PAYS = 3;
export const P_START = 0.08;
/** Chance a START spin comes up three of a kind. */
export const P_FEVER = 1 / 30;
/** Balls paid by a FEVER: the rest of the return, all in the jackpot. */
export const FEVER_PAYS = Math.round((TARGET_RTP - P_TULIP * TULIP_PAYS - P_START) / (P_START * P_FEVER));

export type BallEnd = 'out' | 'tulip' | 'start';

export interface Spin {
  reels: [number, number, number];
  fever: boolean;
}

export interface Batch {
  balls: BallEnd[];
  /** One spin per START ball, in the order the balls land. */
  spins: Spin[];
}

export interface PachinkoState {
  phase: 'idle' | 'done';
  batch: Batch | null;
  /** The ★'s second chance: the losing batch it replaced (shown first). */
  lost: Batch | null;
  bet: number;
  returned: number;
  lucky: boolean;
}

/** Balls a batch pays back. */
export function ballsBack(b: Batch): number {
  let n = 0;
  for (const end of b.balls) n += end === 'tulip' ? TULIP_PAYS : end === 'start' ? 1 : 0;
  for (const s of b.spins) if (s.fever) n += FEVER_PAYS;
  return n;
}

function spin(rng: Rng): Spin {
  if (rng.next() < P_FEVER) {
    const d = rng.int(9) + 1;
    return { reels: [d, d, d], fever: true };
  }
  const a = rng.int(9) + 1;
  const b = rng.int(9) + 1;
  let c = rng.int(9) + 1;
  // Not three of a kind (that's FEVER); two of a kind is a "reach".
  if (a === b && c === a) c = (c % 9) + 1;
  return { reels: [a, b, c], fever: false };
}

function fire(rng: Rng): Batch {
  const balls: BallEnd[] = [];
  const spins: Spin[] = [];
  for (let i = 0; i < BALLS; i++) {
    const r = rng.next();
    const end: BallEnd = r < P_TULIP ? 'tulip' : r < P_TULIP + P_START ? 'start' : 'out';
    balls.push(end);
    if (end === 'start') spins.push(spin(rng));
  }
  return { balls, spins };
}

/** One ball's results (balls back, per ball fired). */
const BALL_OUTCOMES: Outcome[] = [
  [1 - P_TULIP - P_START, 0],
  [P_TULIP, TULIP_PAYS],
  [P_START * (1 - P_FEVER), 1],
  [P_START * P_FEVER, 1 + FEVER_PAYS],
];

let batchOutcomes: Outcome[] | null = null;
/** A whole play's results, per $1 bet (for the ★'s second-chance rate):
 *  every way BALLS balls can land, summed by the balls they pay back. */
export function pachinkoOutcomes(): Outcome[] {
  if (batchOutcomes) return batchOutcomes;
  let dist = new Map<number, number>([[0, 1]]);
  for (let i = 0; i < BALLS; i++) {
    const next = new Map<number, number>();
    for (const [total, p] of dist) for (const [q, r] of BALL_OUTCOMES) next.set(total + r, (next.get(total + r) ?? 0) + p * q);
    dist = next;
  }
  batchOutcomes = [...dist].map(([total, p]) => [p, total / BALLS] as const);
  return batchOutcomes;
}

export const pachinko: CasinoGame<PachinkoState> = {
  id: PACHINKO,
  noDealer: true,
  usesLuck: true,
  name: 'Pachinko',
  blurb: '50 balls through the pins. Hit START, spin for FEVER, and it rains.',
  baseMinBet: 10,
  baseMaxBet: 100,
  betStep: 10,
  specialPerk: 'Luck: a losing batch is sometimes fired again.',
  jackpotRule: 'Three FEVERs in one play.',
  create: () => ({ phase: 'idle', batch: null, lost: null, bet: 0, returned: 0, lucky: false }),
  locked: () => false,
  options: (state) => [{ id: 'fire', label: state.phase === 'done' ? 'Fire again' : `Fire ${BALLS} balls` }],
  describe(state) {
    if (!state.batch) return `Fire ${BALLS} balls. Tulips pay ${TULIP_PAYS}; START spins the screen; three of a kind is FEVER (${FEVER_PAYS} balls).`;
    const n = ballsBack(state.batch);
    return `${n} balls back · ${state.returned - state.bet >= 0 ? '+' : '-'}$${Math.abs(state.returned - state.bet)}`;
  },
  act(state, optionId, env) {
    if (optionId !== 'fire') return { state, bankroll: env.bankroll };
    const q = secondChanceRate(pachinkoOutcomes(), luckOf(env));
    const { outcome: batch, lucky, first } = drawWithLuck(env.rng, () => fire(env.rng), (b) => ballsBack(b) < BALLS, q);
    const returned = Math.round((env.bet / BALLS) * ballsBack(batch));
    const fevers = batch.spins.filter((s) => s.fever).length;
    return {
      state: { phase: 'done', batch, lost: first ?? null, bet: env.bet, returned, lucky },
      bankroll: env.bankroll - env.bet + returned,
      settled: { wagered: env.bet, returned },
      jackpot: fevers >= 3,
      // The web draws the balls itself; this frame just holds the display
      // (and marks a ★ second chance for the screen to find).
      frames: first ? ['Balls away...', secondChanceFrame('No luck...')] : ['Balls away...'],
    };
  },
};
